import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent, unref as _unref, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "grading-view-container" }
const _hoisted_2 = { class: "top-bar" }
const _hoisted_3 = { class: "top-bar-content" }
const _hoisted_4 = { class: "left-section" }
const _hoisted_5 = { class: "model-selector-container" }
const _hoisted_6 = ["title"]
const _hoisted_7 = { class: "model-name" }
const _hoisted_8 = { class: "image-status" }
const _hoisted_9 = { class: "sections-container" }
const _hoisted_10 = ["data-section-id"]
const _hoisted_11 = { key: 0 }
const _hoisted_12 = {
  key: 1,
  class: "loading-error"
}
const _hoisted_13 = {
  key: 2,
  class: "loading-error"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { class: "section-wrapper" }
const _hoisted_16 = { class: "section-content" }
const _hoisted_17 = { key: 2 }
const _hoisted_18 = { class: "section-wrapper" }
const _hoisted_19 = { class: "section-content" }
const _hoisted_20 = { key: 3 }
const _hoisted_21 = { class: "section-wrapper" }
const _hoisted_22 = { class: "section-content peer-gallery-content" }
const _hoisted_23 = ["src"]
const _hoisted_24 = { key: 4 }
const _hoisted_25 = { class: "modal-header" }
const _hoisted_26 = { class: "header-checkbox-label" }
const _hoisted_27 = { class: "modal-body" }
const _hoisted_28 = { class: "model-list" }
const _hoisted_29 = ["onClick"]
const _hoisted_30 = { class: "model-name" }
const _hoisted_31 = {
  key: 0,
  class: "model-description"
}

import AssignmentDetails from "@/components/AssignmentDetails.vue";
import BaseIcon from "@/components/BaseIcon.vue";
import ChatWindow from "@/components/ChatWindow.vue";
import FinalizationEditor from "@/components/FinalizationEditor.vue";
import ImageViewer from "@/components/ImageViewer.vue";
import PastReviewExamples from "@/components/PastReviewExamples.vue";
import SectionWrapper from "@/components/SectionWrapper.vue";
import SystemPrompt from "@/components/SystemPrompt.vue";
import UserSection from "@/components/UserSection.vue";
import { useAuthStore } from "@/stores";
import { useAssessmentStore } from "@/stores/assessment";
import {
  AssessmentState,
  defaultProfileStates,
  SavedSection,
} from "@/stores/types";
import {
  computed,
  nextTick,
  onMounted,
  ref,
  shallowRef,
  watch,
  type Component,
} from "vue";
import { useRouter } from "vue-router";
import draggable from "vuedraggable";
import type { Message } from "../types/chat";

interface Props {
  id: string;
}

interface Section {
  id: string;
  title: string;
  component: string;
  isCollapsed: boolean;
  isDisabled: boolean;
}

interface ImageObject {
  url: string;
  title: string;
  fileType: string;
}

interface ModelInfo {
  id: string;
  name: string;
  description?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GradingView',
  props: {
    id: {}
  },
  setup(__props: any) {

const props = __props;

// Assessment state computed property (Pinia-based)
const assessmentState = computed<AssessmentState>(() => {
  // Get from Pinia cache first for immediate rendering
  const cached = authStore.getCurrentProfileAssessmentStates[props.id];
  if (cached) {
    return cached;
  }

  // Fallback to default state
  return {
    ...defaultProfileStates.assessmentState,
  };
});

// Helper functions for assessment state operations
// TODO: Deprecated, do not use. Is not type safe!
const updateAssessmentState = (updates: Record<string, unknown>) => {
  authStore.updateAssessmentStateDebounced(props.id, updates);
};

// Helper functions for profile preferences
const updatePreference = (key: string, value: unknown) => {
  if (authStore.currentProfile) {
    const currentPreferences = authStore.currentProfile.state.preferences || {};
    const updatedProfile = {
      ...authStore.currentProfile,
      state: {
        ...authStore.currentProfile.state,
        preferences: {
          ...currentPreferences,
          [key]: value,
        },
      },
    };
    authStore.setProfile(updatedProfile);
  }
};

const resetProfileState = () => {
  if (authStore.currentProfile) {
    const resetProfile = {
      ...authStore.currentProfile,
      state: {},
    };
    authStore.setProfile(resetProfile);
  }
};

const assessmentStore = useAssessmentStore();
const authStore = useAuthStore();
const router = useRouter();
const assessmentData = computed(() => assessmentStore.assessmentData);

// Menu items for the UserSection component
const userMenuItems = computed(() => [
  {
    id: "system-prompt",
    label: sections.value.find((s) => s.id === "system-prompt")
      ? "Piilota järjestelmäkehote"
      : "Näytä järjestelmäkehote",
    action: toggleSystemPrompt,
  },
  {
    id: "past-review-examples",
    label: sections.value.find((s) => s.id === "past-review-examples")
      ? "Piilota aiemmat esimerkit"
      : "Näytä aiemmat esimerkit",
    action: togglePastReviewExamples,
  },
  {
    id: "reset-layout",
    label: "Palauta oletustilaan",
    action: () => void resetLayout(),
  },
]);

const fetchError = ref(false);
const isSubmitting = ref(false);

const sections = ref<Section[]>([]);
const isDragging = ref(false);

// Helper function to scroll to a newly added section
const scrollToNewSection = (sectionId: string, delay = 0) => {
  setTimeout(() => {
    void nextTick(() => {
      scrollToSection(sectionId);
    });
  }, delay);
};

// Utility function to calculate text statistics (same as in ChatWindow)
const calculateTextStats = (content: string) => {
  // If content has HTML tags, extract plain text
  let plainText: string;
  if (content.includes("<") && content.includes(">")) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    plainText = tempDiv.textContent || tempDiv.innerText || "";
  } else {
    // If it's markdown or plain text, use as-is for word counting
    // Remove markdown formatting for more accurate word count
    plainText = content
      .replace(/[#*_`~[\]()]/g, "") // Remove markdown syntax
      .replace(/![.*?]\(.*?\)/g, "") // Remove image syntax
      .replace(/[.*?]\(.*?\)/g, "") // Remove link syntax
      .trim();
  }

  // Calculate word count (split by whitespace and filter non-empty)
  const words = plainText
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const wordCount = words.length;

  // Calculate character counts
  const characterCount = plainText.length;
  const characterCountNoSpaces = plainText.replace(/\s/g, "").length;

  return {
    wordCount,
    characterCount,
    characterCountNoSpaces,
  };
};

const fullscreenImage = ref<ImageObject | null>(null);

// Winner selection state for Viimeistely section
const selectedWinnerId = ref<string | null>(null);
const selectedWinnerContent = ref<string>("");
const isFinalizationEnabled = computed(() => !!selectedWinnerId.value);
// Separate seed ref passed to the editor — only updated when a winner is
// selected or state is restored, never on every keystroke. This breaks the
// feedback loop that caused the cursor to jump to the end on every edit.
const editorSeedContent = ref<string>("");

// Flag to prevent circular API calls during state restoration
const isRestoringState = ref(false);

// Track if past review examples section should be shown
const showPastReviewExamples = ref(false);
const allImages = ref<ImageObject[]>([]);

// Teacher notes state
const teacherNotes = ref<string>("");

// System prompt ref
const systemPromptRef = ref<InstanceType<typeof SystemPrompt> | null>(null);
// Past review examples ref
const pastReviewExamplesRef = ref<InstanceType<
  typeof PastReviewExamples
> | null>(null);

// Prefetched values used when the panels are not mounted
const fetchedSystemPrompt = ref("");
const fetchedTeacherExamples = ref("");

const resolvedSystemPrompt = computed(() => {
  if (systemPromptRef.value) {
    return (systemPromptRef.value.getCurrentPrompt() as string) || "";
  }
  return (
    authStore.currentProfile?.state?.preferences?.systemPrompt ||
    fetchedSystemPrompt.value
  );
});

const resolvedTeacherExamples = computed(() => {
  if (pastReviewExamplesRef.value) {
    return (pastReviewExamplesRef.value.getCurrentExamples() as string) || "";
  }
  return (
    authStore.currentProfile?.state?.preferences?.teacherExamples ||
    fetchedTeacherExamples.value
  );
});

// Grading state
const isGradingStarted = ref(false);
const isInitialReviewLoading = ref(false);
const initialReviewMessage = ref<Message | null>(null);
const isChatLoading = ref(false);

// Model selection state
const availableModels = ref<ModelInfo[]>([]);
const selectedModel = ref<string>("gpt-4o-mini");
const showModelSelector = ref(false);
const includeImages = ref<boolean>(true);

// Save grading state whenever it changes (scoped to assessment)
watch(isGradingStarted, (newValue) => {
  if (!isRestoringState.value) {
    updateAssessmentState({ isGradingStarted: newValue });
  }
});

// Save selected model and image inclusion settings to profile preferences
watch(selectedModel, (newValue) => {
  updatePreference("selectedModel", newValue);
});

watch(includeImages, (newValue) => {
  updatePreference("includeImages", newValue);
});

// Save teacher notes with automatic debounced backend sync
watch(teacherNotes, (newValue) => {
  if (!isRestoringState.value) {
    updateAssessmentState({ teacherNotes: newValue });
  }
});

// Save finalization state when any component changes
watch(
  [selectedWinnerId, selectedWinnerContent],
  () => {
    if (!isRestoringState.value) {
      const finalizationState = {
        selectedWinnerId: selectedWinnerId.value,
        selectedWinnerContent: selectedWinnerContent.value,
      };
      updateAssessmentState({ finalizationState });
    }
  },
  { deep: true },
);

// Save past review examples visibility state
watch(showPastReviewExamples, (newValue) => {
  if (!isRestoringState.value) {
    updateAssessmentState({ showPastReviewExamples: newValue });
  }
});

// Save gallery states when they change
watch(fullscreenImage, (newValue) => {
  if (!isRestoringState.value) {
    const galleryState = {
      isOpen: !!newValue,
      currentImageUrl: newValue?.url || null,
    };
    updateAssessmentState({ galleryState });
  }
});

// Separate state for assignment images gallery
const assignmentGalleryImage = ref<ImageObject | null>(null);
watch(assignmentGalleryImage, (newValue) => {
  if (!isRestoringState.value) {
    const assignmentGalleryState = {
      isOpen: !!newValue,
      currentImageUrl: newValue?.url || null,
    };
    updateAssessmentState({ assignmentGalleryState });
  }
});

// Peer gallery state
const showPeerGallery = ref(false);
const peerGalleryUrl = ref<string | null>(null);
// Save peer gallery state when it changes
watch([showPeerGallery, peerGalleryUrl], () => {
  if (!isRestoringState.value) {
    const peerGalleryState = {
      isOpen: showPeerGallery.value,
      galleryUrl: peerGalleryUrl.value,
    };
    updateAssessmentState({ peerGalleryState });
  }
});

// Save layout when sections change (collapsed state, order, etc.)
watch(
  sections,
  () => {
    if (!isRestoringState.value) {
      const layoutData = sections.value.map((section) => ({
        id: section.id,
        isCollapsed: section.isCollapsed,
      }));
      updateAssessmentState({ gradingViewLayout: layoutData });
    }
  },
  { deep: true },
);

const fetchSystemData = async () => {
  const apiBaseUrl =
    (process.env.VUE_APP_API_BASE_URL as string) || "http://localhost:3000/api";
  try {
    const [promptRes, examplesRes] = await Promise.all([
      fetch(`${apiBaseUrl}/system-prompt/${props.id}`, {
        credentials: "include",
      }),
      fetch(`${apiBaseUrl}/teacher-examples/${props.id}`, {
        credentials: "include",
      }),
    ]);
    if (promptRes.ok) {
      const data = (await promptRes.json()) as { systemPrompt?: string };
      fetchedSystemPrompt.value = data.systemPrompt || "";
    }
    if (examplesRes.ok) {
      const data = (await examplesRes.json()) as { teacherExamples?: string };
      fetchedTeacherExamples.value = data.teacherExamples || "";
    }
  } catch (err) {
    console.warn("Failed to prefetch system prompt/examples:", err);
  }
};

// Fetch available models from backend
const fetchAvailableModels = async () => {
  try {
    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) ||
      "http://localhost:3000/api";
    const response = await fetch(`${apiBaseUrl}/models`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch models");
    }

    const data = (await response.json()) as { models: ModelInfo[] };
    availableModels.value = data.models;
  } catch (error) {
    console.error("Error fetching models:", error);
    // Fallback models if API fails
    availableModels.value = [
      {
        id: "gpt-4o-mini",
        name: "GPT-4o Mini",
        description: "Fast and cost-effective",
      },
      { id: "gpt-4o", name: "GPT-4o", description: "Most capable model" },
    ];
  }
};

// Assignment context for AI
const assignmentContext = computed(() => {
  if (!assessmentData.value) return undefined;
  return {
    title:
      assessmentData.value.learningUnit?.title ||
      assessmentData.value.section?.title,
    description:
      assessmentData.value.assignmentDetails?.tehtävänanto ||
      assessmentData.value.section?.description,
    criteria: assessmentData.value.assignmentDetails?.arviointiperusteet || [],
  };
});

const allAssignmentImages = ref<ImageObject[]>([]);

// Use shallowRef for components to avoid deep reactivity overhead
const componentMap = shallowRef<Record<string, Component>>({
  AssignmentDetails,
  ChatWindow,
  FinalizationEditor,
  SystemPrompt,
});

const getComponent = (componentName: string): Component | undefined => {
  return componentMap.value[componentName];
};

const getSectionDefaults = (id: string): Section => {
  const defaults = {
    details: {
      title: "Tehtävän tiedot",
      component: "AssignmentDetails",
    },
    chat: {
      title: "Arviointi",
      component: "ChatWindow",
      isCollapsed: true,
      isDisabled: true,
    },
    viimeistely: {
      title: "Viimeistely",
      component: "FinalizationEditor",
      isCollapsed: true,
      isDisabled: true,
    },
    gallery: {
      title: "Opiskelijan kuvat",
      component: "ImageViewer",
    },
    "assignment-gallery": {
      title: "Tehtävän kuvat",
      component: "ImageViewer",
    },
    "peer-gallery": {
      title: "Kotitehtävägalleria",
      component: "PeerGallery",
    },
    "system-prompt": {
      title: "Järjestelmäkehote",
      component: "SystemPrompt",
    },
    "past-review-examples": {
      title: "Aiemmat arviointiesimerkit",
      component: "PastReviewExamples",
    },
  };
  return {
    ...{
      id,
      title: id,
      component: "Unknown",
      isCollapsed: false,
      isDisabled: false,
    },
    ...defaults[id as keyof typeof defaults],
  };
};

const initializeSections = () => {
  try {
    const savedLayout = assessmentState.value.gradingViewLayout;

    if (savedLayout && Array.isArray(savedLayout) && savedLayout.length > 0) {
      // Reconstruct full section objects from saved data
      sections.value = savedLayout.map((savedSection: SavedSection) => {
        const defaults = getSectionDefaults(savedSection.id);
        return {
          ...defaults,
          ...savedSection,
        };
      });
    } else {
      sections.value = ["details", "chat"].map((sectionId) => {
        const defaults = getSectionDefaults(sectionId);
        return {
          ...defaults,
        };
      });
    }
  } catch (e) {
    console.error("Failed to restore layout from profile state", e);
  }
};

const saveLayout = () => {
  // This function is now mostly redundant since individual watchers
  // handle automatic saving of all state changes.
  // Keeping it for backwards compatibility and explicit save calls.
  console.log(
    "💾 Layout save triggered - automatic watchers handle most saving now",
  );
};

const toggleSection = (id: string) => {
  // SystemPrompt is not collapsible, only closable
  if (id === "system-prompt") {
    return;
  }

  const section = sections.value.find((s) => s.id === id);
  if (section) {
    const wasCollapsed = section.isCollapsed;
    section.isCollapsed = !section.isCollapsed;
    saveLayout();

    // If section was just expanded (opened), scroll to it
    if (wasCollapsed && !section.isCollapsed) {
      void nextTick(() => {
        scrollToSection(id);
      });
    }
  }
};

const onDragEnd = () => {
  isDragging.value = false;
  saveLayout();
};

const restoreGalleryState = () => {
  if (!assessmentData.value) return;

  try {
    isRestoringState.value = true;
    // Restore student gallery state
    const galleryState = assessmentState.value.galleryState;
    console.log("Restoring gallery state:", galleryState);
    if (galleryState) {
      console.log("Parsed gallery state:", galleryState);
      if (galleryState?.isOpen && galleryState?.currentImageUrl) {
        // Find the image in assessment data
        const images =
          (assessmentData.value?.answers?.[0]?.answerData
            ?.downloads as ImageObject[]) || [];
        console.log("Available images:", images.length);
        const image = images.find(
          (img) => img.url === galleryState?.currentImageUrl,
        );
        console.log("Found matching image:", !!image);
        if (image) {
          allImages.value = images;
          fullscreenImage.value = image;
          console.log("Gallery state restored successfully");
        }
      }
    }

    // Restore assignment gallery state
    const { galleryState: assignmentGalleryState } = assessmentState.value;
    if (assignmentGalleryState) {
      if (
        assignmentGalleryState.isOpen &&
        assignmentGalleryState.currentImageUrl
      ) {
        // Find the image in assignment images
        const assignmentImages =
          assessmentData.value?.assignmentDetails?.kuvat || [];
        if (assignmentImages.includes(assignmentGalleryState.currentImageUrl)) {
          // Create image object and all assignment images array
          const imageObject = {
            url: assignmentGalleryState.currentImageUrl,
            title: "Tehtävän kuva",
            fileType: "image/jpeg",
          };

          const allAssignmentImagesArray = assignmentImages.map(
            (url: string, index: number) => ({
              url: url,
              title: `Tehtävän kuva ${index + 1}`,
              fileType: "image/jpeg",
            }),
          );

          allAssignmentImages.value = allAssignmentImagesArray;
          assignmentGalleryImage.value = imageObject;
          console.log("Assignment gallery state restored successfully");
        }
      }
    }

    // Restore peer gallery state
    const peerGalleryState = assessmentState.value.peerGalleryState;
    if (peerGalleryState?.isOpen && peerGalleryState?.galleryUrl) {
      showPeerGallery.value = true;
      peerGalleryUrl.value = peerGalleryState.galleryUrl;

      // Add peer gallery section if it doesn't exist
      const peerGalleryExists = sections.value.find(
        (s) => s.id === "peer-gallery",
      );
      if (!peerGalleryExists) {
        const detailsIndex = sections.value.findIndex(
          (s) => s.id === "details",
        );
        const insertIndex = detailsIndex !== -1 ? detailsIndex + 1 : 1;

        sections.value.splice(
          insertIndex,
          0,
          getSectionDefaults("peer-gallery"),
        );
        scrollToNewSection("peer-gallery", 150);
      }
      console.log("Peer gallery state restored successfully");
    }
  } catch (e) {
    console.error("Failed to restore gallery state:", e);
    updateAssessmentState({ galleryState: undefined });
    updateAssessmentState({ assignmentGalleryState: undefined });
    updateAssessmentState({ peerGalleryState: undefined });
  } finally {
    isRestoringState.value = false;
  }
};

const openImageGallery = (image: ImageObject) => {
  allImages.value =
    (assessmentData.value?.answers?.[0]?.answerData
      ?.downloads as ImageObject[]) || [];
  fullscreenImage.value = image;

  // Add gallery section if it doesn't exist
  const galleryExists = sections.value.find((s) => s.id === "gallery");
  if (!galleryExists) {
    // Find the index of the details section and insert gallery right after it
    const detailsIndex = sections.value.findIndex((s) => s.id === "details");
    const insertIndex = detailsIndex !== -1 ? detailsIndex + 1 : 1;

    sections.value.splice(insertIndex, 0, getSectionDefaults("gallery"));
    scrollToNewSection("gallery", 150);
  }

  // Save the layout and gallery state immediately
  saveLayout();
};

const openAssignmentGallery = (image: ImageObject, images: ImageObject[]) => {
  allAssignmentImages.value = images;
  assignmentGalleryImage.value = image;

  // Add assignment gallery section if it doesn't exist
  const assignmentGalleryExists = sections.value.find(
    (s) => s.id === "assignment-gallery",
  );
  if (!assignmentGalleryExists) {
    // Find the index to insert assignment gallery (after details but before regular gallery if it exists)
    const detailsIndex = sections.value.findIndex((s) => s.id === "details");
    const galleryIndex = sections.value.findIndex((s) => s.id === "gallery");

    let insertIndex;
    if (galleryIndex !== -1) {
      // Insert before regular gallery
      insertIndex = galleryIndex;
    } else {
      // Insert after details
      insertIndex = detailsIndex !== -1 ? detailsIndex + 1 : 1;
    }

    sections.value.splice(
      insertIndex,
      0,
      getSectionDefaults("assignment-gallery"),
    );
    scrollToNewSection("assignment-gallery", 150);
  }

  // Save the layout immediately
  saveLayout();
};

const closeImageGallery = () => {
  fullscreenImage.value = null;

  // Remove gallery section
  sections.value = sections.value.filter((s) => s.id !== "gallery");

  // Clear gallery state from profile state
  updateAssessmentState({ galleryState: undefined });
  saveLayout();
};

const closeAssignmentGallery = () => {
  assignmentGalleryImage.value = null;

  // Remove assignment gallery section
  sections.value = sections.value.filter((s) => s.id !== "assignment-gallery");

  // Save layout
  saveLayout();
};

const togglePeerGallery = (galleryUrl: string) => {
  if (showPeerGallery.value && peerGalleryUrl.value) {
    closePeerGallery();
  } else {
    openPeerGallery(galleryUrl);
  }
};

const openPeerGallery = (galleryUrl: string) => {
  peerGalleryUrl.value = galleryUrl;
  showPeerGallery.value = true;

  // Add peer gallery section if it doesn't exist
  const peerGalleryExists = sections.value.find((s) => s.id === "peer-gallery");
  if (!peerGalleryExists) {
    // Find the index of the details section and insert peer gallery right after it
    const detailsIndex = sections.value.findIndex((s) => s.id === "details");
    const insertIndex = detailsIndex !== -1 ? detailsIndex + 1 : 1;

    sections.value.splice(insertIndex, 0, getSectionDefaults("peer-gallery"));
    scrollToNewSection("peer-gallery", 150);
  }

  // Save the layout immediately
  saveLayout();
};

const closePeerGallery = () => {
  showPeerGallery.value = false;
  peerGalleryUrl.value = null;

  // Remove peer gallery section
  sections.value = sections.value.filter((s) => s.id !== "peer-gallery");

  // Clear peer gallery state from profile state
  updateAssessmentState({ peerGalleryState: undefined });
  saveLayout();
};

const onStudentImageChanged = (image: ImageObject) => {
  fullscreenImage.value = image;
  saveLayout(); // Save the new current image
};

const onAssignmentImageChanged = (image: ImageObject) => {
  assignmentGalleryImage.value = image;
  saveLayout(); // Save the new current image
};

const onStartGrading = async (notes: string) => {
  isGradingStarted.value = true;
  isInitialReviewLoading.value = true;

  // Auto-expand the Arviointi section when grading starts
  const chatSection = sections.value.find((s) => s.id === "chat");
  if (chatSection && chatSection.isCollapsed) {
    chatSection.isCollapsed = false;
    saveLayout();
  }

  // Scroll to the Arviointi section and focus the chat input
  void nextTick(() => {
    scrollToSection("chat");
    // Focus the chat input after a short delay to ensure it's rendered
    setTimeout(() => {
      const chatInput = document.querySelector(
        ".chat-window .message-input textarea",
      ) as HTMLTextAreaElement;
      if (chatInput) {
        chatInput.focus();
      }
    }, 300);
  });

  try {
    // Get assessment ID from route props
    const assessmentId = props.id;

    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) || "http://localhost:3000";
    const response = await fetch(
      `${apiBaseUrl}/assessment_responses/${assessmentId}/review`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          message:
            "Aloita arviointi. Anna yleinen arvio opiskelijan työstä ja ehdota keskusteluaiheita jatkoarviointia varten.",
          teacherNotes: notes,
          assignmentContext: assignmentContext.value,
          systemPrompt: resolvedSystemPrompt.value,
          model: selectedModel.value,
          teacherExamples: resolvedTeacherExamples.value,
          includeImages: includeImages.value,
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Alkuarvioinnin lataus epäonnistui.");
    }

    const apiResponse = (await response.json()) as {
      id: string;
      sender: string;
      content: string;
      timestamp: string;
    };
    initialReviewMessage.value = {
      ...apiResponse,
      timestamp: new Date(apiResponse.timestamp),
      stats: calculateTextStats(apiResponse.content),
      originalMarkdown: apiResponse.content, // Store original markdown
    } as Message;
  } catch (error) {
    console.error("Error getting initial review:", error);
    initialReviewMessage.value = {
      id: `error-${Date.now()}`,
      sender: "ai",
      content:
        "<p>Pahoittelut, alkuarvioinnin lataus epäonnistui. Voit kuitenkin aloittaa keskustelun normaalisti.</p>",
      timestamp: new Date(),
    };
  } finally {
    isInitialReviewLoading.value = false;
  }
};

const onResetGrading = () => {
  // Reset grading state
  isGradingStarted.value = false;
  isInitialReviewLoading.value = false;
  initialReviewMessage.value = null;

  // We do not clear teacher's notes so, that the teacher can use the previous notes as the basis for new notes and doesn't need to start from scratch

  // Clear scoped assessment state for grading-related data
  // IMPORTANT: DO NOT CLEAR THE TEACHERS NOTES HERE!!!
  // We just want to allow editing them, but keep the old data for the sake of convenience. Likely the teacher only wants to do some minor edit or addition to them instead of a complete rewrite.
  authStore.updateAssessmentStateDebounced(props.id, {
    isGradingStarted: false,
    chatMessages: [],
    finalizationState: {
      selectedWinnerId: "",
      selectedWinnerContent: "",
    },
    showPastReviewExamples: false,
  });

  // Collapse and disable the Arviointi section
  const chatSection = sections.value.find((s) => s.id === "chat");
  if (chatSection) {
    chatSection.isCollapsed = true;
    chatSection.isDisabled = true;
  }

  // Save the layout changes
  saveLayout();

  // Reset winner selection and remove Viimeistely section
  selectedWinnerId.value = null;
  selectedWinnerContent.value = "";

  // Reset past review examples state
  showPastReviewExamples.value = false;

  // Remove Viimeistely, past review examples, and peer gallery sections if they exist
  sections.value = sections.value.filter(
    (s) =>
      s.id !== "viimeistely" &&
      s.id !== "past-review-examples" &&
      s.id !== "peer-gallery",
  );

  // Reset peer gallery state
  showPeerGallery.value = false;
  peerGalleryUrl.value = null;
};

const updateTeacherNotes = (notes: string) => {
  teacherNotes.value = notes;
  // Notes are automatically saved via watcher with debouncing
};

const updateGalleryLink = (newLink: string | null) => {
  // Update the assessmentData with the new gallery link
  if (assessmentData.value?.assignmentDetails) {
    assessmentData.value.assignmentDetails.peerGalleryDriveLink = newLink;
  }
};

const onAssignmentConfirmed = (details: {
  id: number;
  isConfirmed: boolean;
  tehtävänanto: string;
  arviointiperusteet: string[];
  kuvat: string[];
  peerGalleryDriveLink: null;
}) => {
  if (assessmentData.value) {
    assessmentData.value.assignmentDetails = details;
  }
};

const restoreTeacherNotes = () => {
  try {
    isRestoringState.value = true;
    const savedNotes = assessmentState.value.teacherNotes;
    if (savedNotes) {
      teacherNotes.value = savedNotes;
    }
  } catch (e) {
    console.error("Failed to restore teacher notes:", e);
  } finally {
    isRestoringState.value = false;
  }
};

const restoreGradingState = () => {
  try {
    isRestoringState.value = true;
    const savedState = assessmentState.value.isGradingStarted;
    if (savedState !== null) {
      isGradingStarted.value = savedState;
    }
  } catch (e) {
    console.error("Failed to restore grading state:", e);
    updateAssessmentState({ isGradingStarted: undefined });
  } finally {
    isRestoringState.value = false;
  }
};

const restoreFinalizationState = () => {
  try {
    isRestoringState.value = true;
    const savedState = assessmentState.value.finalizationState;
    console.log(
      "🔄 Restoring finalization state from profile state:",
      savedState,
    );
    if (savedState) {
      console.log("🔄 Parsed finalization state:", savedState);
      selectedWinnerId.value = savedState.selectedWinnerId;
      selectedWinnerContent.value = savedState.selectedWinnerContent;
      editorSeedContent.value = savedState.selectedWinnerContent;
      console.log("🔄 Applied finalization state:", {
        selectedWinnerId: selectedWinnerId.value,
        selectedWinnerContent: selectedWinnerContent.value,
        isFinalizationEnabled: isFinalizationEnabled.value,
      });

      // If finalization was enabled, ensure the Viimeistely section exists
      if (savedState?.selectedWinnerId) {
        const viimeistelyExists = sections.value.find(
          (s) => s.id === "viimeistely",
        );
        if (!viimeistelyExists) {
          // Find the index of the chat section and insert Viimeistely right after it
          const chatIndex = sections.value.findIndex((s) => s.id === "chat");
          const insertIndex =
            chatIndex !== -1 ? chatIndex + 1 : sections.value.length;

          sections.value.splice(insertIndex, 0, {
            id: "viimeistely",
            title: "Viimeistely",
            component: "FinalizationEditor",
            isCollapsed: false,
            isDisabled: false,
          });
        }
      }
    }
  } catch (e) {
    console.error("Failed to restore finalization state:", e);
    updateAssessmentState({ finalizationState: undefined });
  } finally {
    isRestoringState.value = false;
  }
};

const restorePastReviewExamplesState = () => {
  try {
    isRestoringState.value = true;
    const savedState = assessmentState.value.showPastReviewExamples;
    if (savedState !== null) {
      showPastReviewExamples.value = savedState;

      // If past review examples should be shown, ensure the section exists and is not filtered out
      if (showPastReviewExamples.value) {
        const examplesExists = sections.value.find(
          (s) => s.id === "past-review-examples",
        );
        if (!examplesExists) {
          // Add the section at the beginning
          const pastReviewExamplesSection = getSectionDefaults(
            "past-review-examples",
          );
          sections.value.unshift(pastReviewExamplesSection);
        }
      } else {
        // Remove the section if it shouldn't be shown
        sections.value = sections.value.filter(
          (s) => s.id !== "past-review-examples",
        );
      }
    }
  } catch (e) {
    console.error("Failed to restore past review examples state:", e);
    updateAssessmentState({ showPastReviewExamples: undefined });
  } finally {
    isRestoringState.value = false;
  }
};

// Winner selection for Viimeistely section
const selectWinner = (messageId: string, content: string) => {
  console.log("🏆 selectWinner called:", {
    messageId,
    contentLength: content.length,
  });

  // Check if we're changing selection and finalization is in progress
  const isChangingSelection =
    selectedWinnerId.value && selectedWinnerId.value !== messageId;
  const hasFinalizationContent =
    selectedWinnerContent.value &&
    selectedWinnerContent.value.trim() !== content.trim();

  if (isChangingSelection && hasFinalizationContent) {
    const confirmed = window.confirm(
      "Olet vaihtamassa valittua viestiä viimeistelyyn. Tämä toiminto poistaa nykyiset viimeistelyn muutokset.\n\n" +
        "Haluatko varmasti jatkaa? Nykyinen viimeisteltytyösi menetetään.",
    );

    if (!confirmed) {
      console.log("🚫 Winner selection change cancelled by user");
      return;
    }

    console.log(
      "✅ User confirmed winner selection change, clearing finalization data",
    );
  }

  // Update winner selection
  selectedWinnerId.value = messageId;
  selectedWinnerContent.value = content;
  editorSeedContent.value = content;
  console.log("🏆 Winner state updated:", {
    selectedWinnerId: selectedWinnerId.value,
    selectedWinnerContentLength: selectedWinnerContent.value.length,
    isFinalizationEnabled: isFinalizationEnabled.value,
  });

  // Add Viimeistely section if it doesn't exist
  const viimeistelyExists = sections.value.find((s) => s.id === "viimeistely");
  if (!viimeistelyExists) {
    // Find the index of the chat section and insert Viimeistely right after it
    const chatIndex = sections.value.findIndex((s) => s.id === "chat");
    const insertIndex =
      chatIndex !== -1 ? chatIndex + 1 : sections.value.length;

    sections.value.splice(insertIndex, 0, {
      id: "viimeistely",
      title: "Viimeistely",
      component: "FinalizationEditor",
      isCollapsed: false,
      isDisabled: false,
    });

    saveLayout();

    // Scroll to the new section
    void nextTick(() => {
      scrollToSection("viimeistely");
    });
  }
};

// Handle content changes in FinalizationEditor
const updateFinalizationContent = (content: string) => {
  console.log("📝 FinalizationEditor content changed:", {
    contentLength: content.length,
  });
  selectedWinnerContent.value = content;
  // Save layout to persist the content change
  saveLayout();
};

// Handle final review submission
const submitFinalReview = async (content: string) => {
  // Prevent double submission
  if (isSubmitting.value) {
    console.log("Submission already in progress, ignoring duplicate request");
    return;
  }

  isSubmitting.value = true;

  try {
    console.log("Submitting final review:", content.substring(0, 100) + "...");

    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) || "http://localhost:3000";

    const response = await fetch(
      `${apiBaseUrl}/assessment_responses/${props.id}/submit`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ content }),
      },
    );

    if (!response.ok) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const errorData = await response.json();

      // Handle specific error cases
      if (response.status === 409) {
        // Already submitted - redirect without showing error
        console.log("Assessment already submitted, redirecting to list");
        await router.push({
          path: "/assessments",
          query: { submitted: "true" } as Record<string, string>,
        });
        return;
      }

      if (response.status === 502) {
        // LearnWorlds API failure - show error with retry option
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const errorMsg = errorData.details
          ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            `LearnWorlds API -virhe: ${errorData.details as string}`
          : "Palvelimen virhe lähetettäessä arviointia LearnWorlds-järjestelmään.";

        if (
          confirm(
            `${errorMsg}\n\nHaluatko yrittää uudelleen?\n\nKlikkaa OK yrittääksesi uudelleen tai Cancel peruuttaaksesi.`,
          )
        ) {
          isSubmitting.value = false;
          // Retry by calling the function again
          await submitFinalReview(content);
          return;
        } else {
          isSubmitting.value = false;
          return;
        }
      }

      // Other errors
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      throw new Error(errorData.error || "Failed to submit final review");
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = await response.json();
    console.log("Final review submitted successfully:", result);

    // Navigate back to assessments list with success state
    // The backend automatically excludes completed assessments from future queries
    await router.push({
      path: "/assessments",
      query: { submitted: "true" } as Record<string, string>,
    });
  } catch (error) {
    console.error("Failed to submit final review:", error);
    alert(
      `Virhe arvioinnin lähettämisessä: ${error instanceof Error ? error.message : "Tuntematon virhe"}\n\nYritä uudelleen.`,
    );
  } finally {
    isSubmitting.value = false;
  }
};

const toggleSystemPrompt = () => {
  const existingSection = sections.value.find((s) => s.id === "system-prompt");

  if (existingSection) {
    // Remove system prompt section
    sections.value = sections.value.filter((s) => s.id !== "system-prompt");
  } else {
    // Add system prompt section
    const systemPromptSection = {
      id: "system-prompt",
      title: "Järjestelmäkehote",
      component: "SystemPrompt",
      isCollapsed: false,
      isDisabled: false,
    };
    sections.value.unshift(systemPromptSection);
    scrollToNewSection("system-prompt");
  }

  saveLayout();
};

const togglePastReviewExamples = () => {
  // Toggle the state variable
  showPastReviewExamples.value = !showPastReviewExamples.value;

  if (showPastReviewExamples.value) {
    // Add past review examples section
    const pastReviewExamplesSection = {
      id: "past-review-examples",
      title: "Aiemmat arviointiesimerkit",
      component: "PastReviewExamples",
      isCollapsed: false,
      isDisabled: false,
    };
    // Add after system prompt if it exists, otherwise at the beginning
    const systemPromptIndex = sections.value.findIndex(
      (s) => s.id === "system-prompt",
    );
    if (systemPromptIndex !== -1) {
      sections.value.splice(
        systemPromptIndex + 1,
        0,
        pastReviewExamplesSection,
      );
    } else {
      sections.value.unshift(pastReviewExamplesSection);
    }
    scrollToNewSection("past-review-examples");
  } else {
    // Remove past review examples section
    sections.value = sections.value.filter(
      (s) => s.id !== "past-review-examples",
    );
  }

  saveLayout();
};

const closeSection = (sectionId: string) => {
  // Remove section when close button is clicked
  sections.value = sections.value.filter((s) => s.id !== sectionId);

  // Update state variables when sections are closed
  if (sectionId === "past-review-examples") {
    showPastReviewExamples.value = false;
  }

  saveLayout();
};

const scrollToSection = (sectionId: string) => {
  // Find the section element and scroll to it
  const sectionElement = document.querySelector(
    `[data-section-id="${sectionId}"]`,
  );
  if (sectionElement) {
    sectionElement.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};

const resetLayout = () => {
  // Clear all saved profile state data
  resetProfileState();

  // Reset state to defaults
  teacherNotes.value = "";
  fullscreenImage.value = null;
  assignmentGalleryImage.value = null;
  isGradingStarted.value = false;
  initialReviewMessage.value = null;

  // Reset system prompt to default if component is mounted
  if (systemPromptRef.value) {
    systemPromptRef.value.resetSystemPrompt();
  }
  // Reset past review examples to default if component is mounted
  if (pastReviewExamplesRef.value) {
    pastReviewExamplesRef.value.resetExamples();
  }

  // Reinitialize sections to default layout
  initializeSections();
};

onMounted(async () => {
  // Load assessment state from API to populate Pinia cache first
  // This MUST complete before restoration functions run
  await authStore.getAssessmentState(props.id);

  initializeSections();
  restoreTeacherNotes();
  restoreGradingState();
  restoreFinalizationState();
  restorePastReviewExamplesState();

  // Load saved preferences from profile state
  try {
    const preferences = authStore.currentProfile?.state?.preferences;

    if (preferences?.selectedModel) {
      selectedModel.value = preferences.selectedModel;
    }

    if (preferences?.includeImages !== undefined) {
      includeImages.value = preferences.includeImages;
    }
  } catch (err) {
    console.warn("Failed to load preferences from profile state:", err);
  }

  // Fetch available models and system data
  void fetchAvailableModels();
  void fetchSystemData();

  // Always fetch assessment data for the current assessment ID
  try {
    await assessmentStore.fetchAssessmentData(props.id);
    restoreGalleryState();
  } catch {
    fetchError.value = true;
  }
});

const goBackToAssessments = async () => {
  await router.push("/assessments");
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("button", {
            onClick: goBackToAssessments,
            class: "back-button",
            title: "Takaisin arvioitaviin töihin"
          }, [
            _createVNode(BaseIcon, {
              name: "arrow-left-circle",
              size: 28,
              "class-name": "back-arrow-icon",
              "aria-label": "Takaisin"
            })
          ]),
          _cache[8] || (_cache[8] = _createElementVNode("img", {
            src: "/Teho_aly_logo.svg",
            alt: "Teho-aly",
            class: "logo",
            onerror: "this.src='/Teho_aly_logo.png'"
          }, null, -1))
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showModelSelector.value = true)),
            class: "model-button",
            title: 
              availableModels.value.find((m) => m.id === selectedModel.value)?.description
            
          }, [
            _createElementVNode("span", _hoisted_7, _toDisplayString(availableModels.value.find((m) => m.id === selectedModel.value)?.name ||
                selectedModel.value), 1),
            _cache[9] || (_cache[9] = _createElementVNode("span", { class: "separator" }, "|", -1)),
            _createElementVNode("span", _hoisted_8, _toDisplayString(includeImages.value ? "kuvilla" : "ilman kuvia"), 1)
          ], 8, _hoisted_6)
        ]),
        _createVNode(UserSection, { menuItems: userMenuItems.value }, null, 8, ["menuItems"])
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createVNode(_unref(draggable), {
        modelValue: sections.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((sections).value = $event)),
        "item-key": "id",
        handle: ".section-header",
        onStart: _cache[3] || (_cache[3] = ($event: any) => (isDragging.value = true)),
        onEnd: onDragEnd
      }, {
        item: _withCtx(({ element: section }) => [
          _createElementVNode("div", {
            "data-section-id": section.id
          }, [
            (section.id === 'details')
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createVNode(SectionWrapper, {
                    title: section.title,
                    "is-collapsed": section.isCollapsed,
                    "is-dragging": isDragging.value,
                    onToggle: ($event: any) => (toggleSection(section.id))
                  }, {
                    default: _withCtx(() => [
                      (assessmentData.value)
                        ? (_openBlock(), _createBlock(AssignmentDetails, {
                            key: 0,
                            assignmentData: assessmentData.value,
                            "is-gallery-open": !!fullscreenImage.value,
                            "teacher-notes": teacherNotes.value,
                            "is-grading-started": isGradingStarted.value,
                            "is-peer-gallery-open": !!showPeerGallery.value,
                            onOpenImageGallery: openImageGallery,
                            onCloseImageGallery: closeImageGallery,
                            onOpenAssignmentGallery: openAssignmentGallery,
                            onUpdateTeacherNotes: updateTeacherNotes,
                            onStartGrading: onStartGrading,
                            onResetGrading: onResetGrading,
                            onUpdateGalleryLink: updateGalleryLink,
                            onTogglePeerGallery: togglePeerGallery,
                            onClosePeerGallery: closePeerGallery,
                            onAssignmentConfirmed: onAssignmentConfirmed
                          }, null, 8, ["assignmentData", "is-gallery-open", "teacher-notes", "is-grading-started", "is-peer-gallery-open"]))
                        : (fetchError.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, " Tietojen lataus epäonnistui. "))
                          : (_openBlock(), _createElementBlock("div", _hoisted_13, "Ladataan..."))
                    ]),
                    _: 1
                  }, 8, ["title", "is-collapsed", "is-dragging", "onToggle"])
                ]))
              : (section.id === 'gallery' && fullscreenImage.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["section-header", { 'is-dragging': isDragging.value, 'grab-cursor': true }])
                      }, [
                        _cache[10] || (_cache[10] = _createElementVNode("span", { class: "grip-icon" }, "⠿", -1)),
                        _cache[11] || (_cache[11] = _createElementVNode("h3", null, "Opiskelijan kuvat", -1)),
                        _createElementVNode("button", {
                          class: "close-button",
                          onClick: closeImageGallery
                        }, " ✕ ")
                      ], 2),
                      _createElementVNode("div", _hoisted_16, [
                        _createVNode(ImageViewer, {
                          images: allImages.value,
                          initialImage: fullscreenImage.value,
                          onClose: closeImageGallery,
                          onImageChanged: onStudentImageChanged,
                          "show-controls": false
                        }, null, 8, ["images", "initialImage"])
                      ])
                    ])
                  ]))
                : (
                section.id === 'assignment-gallery' && assignmentGalleryImage.value
              )
                  ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                      _createElementVNode("div", _hoisted_18, [
                        _createElementVNode("div", {
                          class: _normalizeClass(["section-header", { 'is-dragging': isDragging.value, 'grab-cursor': true }])
                        }, [
                          _cache[12] || (_cache[12] = _createElementVNode("span", { class: "grip-icon" }, "⠿", -1)),
                          _cache[13] || (_cache[13] = _createElementVNode("h3", null, "Tehtävän kuvat", -1)),
                          _createElementVNode("button", {
                            class: "close-button",
                            onClick: closeAssignmentGallery
                          }, " ✕ ")
                        ], 2),
                        _createElementVNode("div", _hoisted_19, [
                          _createVNode(ImageViewer, {
                            images: allAssignmentImages.value,
                            initialImage: assignmentGalleryImage.value,
                            onClose: closeAssignmentGallery,
                            onImageChanged: onAssignmentImageChanged,
                            "show-controls": false
                          }, null, 8, ["images", "initialImage"])
                        ])
                      ])
                    ]))
                  : (section.id === 'peer-gallery' && showPeerGallery.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                        _createElementVNode("div", _hoisted_21, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["section-header", { 'is-dragging': isDragging.value, 'grab-cursor': true }])
                          }, [
                            _cache[14] || (_cache[14] = _createElementVNode("span", { class: "grip-icon" }, "⠿", -1)),
                            _cache[15] || (_cache[15] = _createElementVNode("h3", null, "Kotitehtävägalleria", -1)),
                            _createElementVNode("button", {
                              class: "close-button",
                              onClick: closePeerGallery
                            }, " ✕ ")
                          ], 2),
                          _createElementVNode("div", _hoisted_22, [
                            (peerGalleryUrl.value)
                              ? (_openBlock(), _createElementBlock("iframe", {
                                  key: 0,
                                  src: peerGalleryUrl.value,
                                  class: "peer-gallery-iframe",
                                  frameborder: "0",
                                  allowfullscreen: "",
                                  title: "Kotitehtävägalleria"
                                }, null, 8, _hoisted_23))
                              : _createCommentVNode("", true)
                          ])
                        ])
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_24, [
                        _createVNode(SectionWrapper, {
                          title: section.title,
                          "is-collapsed": section.isCollapsed,
                          "is-dragging": isDragging.value,
                          "is-disabled": section.id === 'chat' && !isGradingStarted.value,
                          "show-close-button": 
                  section.id === 'system-prompt' ||
                  section.id === 'past-review-examples'
                ,
                          onToggle: ($event: any) => (toggleSection(section.id)),
                          onClose: ($event: any) => (closeSection(section.id))
                        }, {
                          default: _withCtx(() => [
                            (section.component === 'ChatWindow')
                              ? (_openBlock(), _createBlock(ChatWindow, {
                                  key: 0,
                                  "assessment-id": props.id,
                                  "teacher-notes": teacherNotes.value,
                                  "assignment-context": assignmentContext.value,
                                  "is-initial-loading": isInitialReviewLoading.value,
                                  "initial-message": initialReviewMessage.value,
                                  "system-prompt": resolvedSystemPrompt.value,
                                  "selected-model": selectedModel.value,
                                  "is-loading": isChatLoading.value || isInitialReviewLoading.value,
                                  "teacher-examples": resolvedTeacherExamples.value,
                                  "student-images": allImages.value,
                                  "include-images": includeImages.value,
                                  "can-select-winner": isGradingStarted.value,
                                  "selected-winner-id": selectedWinnerId.value,
                                  onLoadingChanged: _cache[1] || (_cache[1] = ($event: any) => (isChatLoading.value = $event)),
                                  onSelectWinner: selectWinner
                                }, null, 8, ["assessment-id", "teacher-notes", "assignment-context", "is-initial-loading", "initial-message", "system-prompt", "selected-model", "is-loading", "teacher-examples", "student-images", "include-images", "can-select-winner", "selected-winner-id"]))
                              : (section.component === 'SystemPrompt')
                                ? (_openBlock(), _createBlock(SystemPrompt, {
                                    key: 1,
                                    ref_key: "systemPromptRef",
                                    ref: systemPromptRef,
                                    "assessment-id": props.id
                                  }, null, 8, ["assessment-id"]))
                                : (section.component === 'PastReviewExamples')
                                  ? (_openBlock(), _createBlock(PastReviewExamples, {
                                      key: 2,
                                      ref_key: "pastReviewExamplesRef",
                                      ref: pastReviewExamplesRef,
                                      "assessment-id": props.id
                                    }, null, 8, ["assessment-id"]))
                                  : (section.component === 'FinalizationEditor')
                                    ? (_openBlock(), _createBlock(FinalizationEditor, {
                                        key: 3,
                                        "initial-content": editorSeedContent.value,
                                        disabled: !isFinalizationEnabled.value,
                                        "is-submitting": isSubmitting.value,
                                        onContentChange: updateFinalizationContent,
                                        onSubmitReview: submitFinalReview
                                      }, null, 8, ["initial-content", "disabled", "is-submitting"]))
                                    : (_openBlock(), _createBlock(_resolveDynamicComponent(getComponent(section.component)), { key: 4 }))
                          ]),
                          _: 2
                        }, 1032, ["title", "is-collapsed", "is-dragging", "is-disabled", "show-close-button", "onToggle", "onClose"])
                      ]))
          ], 8, _hoisted_10)
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    (showModelSelector.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "modal-overlay",
          onClick: _cache[7] || (_cache[7] = ($event: any) => (showModelSelector.value = false))
        }, [
          _createElementVNode("div", {
            class: "modal-content",
            onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", _hoisted_25, [
              _cache[17] || (_cache[17] = _createElementVNode("h3", null, "Valitse AI-malli", -1)),
              _createElementVNode("label", _hoisted_26, [
                _withDirectives(_createElementVNode("input", {
                  type: "checkbox",
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((includeImages).value = $event)),
                  class: "image-checkbox"
                }, null, 512), [
                  [_vModelCheckbox, includeImages.value]
                ]),
                _cache[16] || (_cache[16] = _createTextVNode(" Sisällytä kuvat ", -1))
              ]),
              _createElementVNode("button", {
                onClick: _cache[5] || (_cache[5] = ($event: any) => (showModelSelector.value = false)),
                class: "close-button"
              }, " × ")
            ]),
            _createElementVNode("div", _hoisted_27, [
              _createElementVNode("div", _hoisted_28, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableModels.value, (model) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: model.id,
                    class: _normalizeClass(["model-option", { selected: model.id === selectedModel.value }]),
                    onClick: ($event: any) => {
                selectedModel.value = model.id;
                showModelSelector.value = false;
              }
                  }, [
                    _createElementVNode("div", _hoisted_30, _toDisplayString(model.name), 1),
                    (model.description)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_31, _toDisplayString(model.description), 1))
                      : _createCommentVNode("", true)
                  ], 10, _hoisted_29))
                }), 128))
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})